import React from "react";
import styled from "styled-components";

import MarkdownContent from "../../../../components/markdown/MarkdownContent";
import Table, { type TableColumn } from "../../../../components/table/Table";
import type { ProducerTask } from "../../../../app/services/taskBoard";
import { isCellComplete, type FilledCell } from "./taskBoardUtils";
import ImageHoverZone from "./ImageHoverZone";

/** 列幅はタスクボードの表（TaskBoardSection の tableColumns）と揃える */
const TITLE_COL_WIDTH = 200;
const CELL_COL_WIDTH = 200;
/** 本文の表示高さの上限。超えた分は表の中を縦スクロール（列ラベルは固定） */
const BODY_MAX_HEIGHT = 360;
/** カード内の画像の表示上限（表のセルと同じく contain で収める） */
const CELL_IMAGE_MAX_HEIGHT_PX = 120;
const TITLE_COL_KEY = "__title";

/** 画面より広い場合は表の中を横スクロールさせる */
const Card = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  max-width: calc(100vw - 48px);
  overflow: hidden;
  border-radius: 8px;
  cursor: default;
`;

const CellBody = styled.div`
  white-space: normal;
  word-break: break-word;
  & p:first-child {
    margin-top: 0;
  }
  & img {
    max-width: 100%;
    max-height: ${CELL_IMAGE_MAX_HEIGHT_PX}px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

interface GanttTaskHoverCardProps {
  task: ProducerTask;
  /** 中身のある列（collectFilledCells の結果） */
  cells: FilledCell[];
  /** タスク全体が完了済みか（タスクボードの完了行と同じく行ごと沈める） */
  isComplete: boolean;
}

/**
 * スケジュール（ガント）の行ラベルにホバーしたときのカード。タスクボードと
 * 同じ横並びの表（タスク名 + 中身のある列）を 1 行で見せる。
 * 閲覧専用（列幅変更・チェックボックス操作は不可）だが、画像の拡大プレビューと
 * リンクのホバーカード・添付チップのプレビューは表と同じく使える。
 */
const GanttTaskHoverCard: React.FC<GanttTaskHoverCardProps> = ({
  task,
  cells,
  isComplete,
}) => {
  const columns: TableColumn[] = [
    {
      key: TITLE_COL_KEY,
      header: "タスク",
      width: TITLE_COL_WIDTH,
      resizable: false,
    },
    ...cells.map(({ column }) => ({
      key: column.id,
      header: column.title || "（無題の列）",
      width: CELL_COL_WIDTH,
      resizable: false,
    })),
  ];
  const tableWidth = TITLE_COL_WIDTH + CELL_COL_WIDTH * cells.length;

  return (
    <Card $width={tableWidth}>
      <Table
        columns={columns}
        minWidth={tableWidth}
        frame="bare"
        maxBodyHeight={BODY_MAX_HEIGHT}
      >
        <tr className={isComplete ? "table-row-muted" : undefined}>
          <td className="bodytext-13">
            <CellBody>{task.title || "（無題）"}</CellBody>
          </td>
          {cells.map(({ column, content }) => (
            <td
              key={column.id}
              className={
                isCellComplete(content) ? "table-cell-dimmed" : undefined
              }
            >
              <CellBody>
                <ImageHoverZone>
                  <MarkdownContent
                    content={content}
                    attachmentDisplay="preview"
                  />
                </ImageHoverZone>
              </CellBody>
            </td>
          ))}
        </tr>
      </Table>
    </Card>
  );
};

export default GanttTaskHoverCard;
